import { useCallback, useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { useKeyedState } from "@/hooks/useKeyedState";

// The legacy key was force-written to `true` by any narrow-viewport visit,
// so its stored values don't reflect a real user choice. A fresh key starts
// everyone from the auto-open default; only explicit wide-screen collapses
// persist under it.
const LEGACY_COLLAPSED_KEY = "assistantSidebarCollapsed";
/** Exported so the loading skeleton can honor a persisted collapse. */
export const SESSION_SIDEBAR_COLLAPSED_KEY = "codesmithSidebarCollapsed";

type SetCollapsed = (value: boolean | ((prev: boolean) => boolean)) => void;

/**
 * Collapsed state for the session sidebar that only auto-opens when the
 * container has room for it (see `usePanelSpaceBudget`). While space exists
 * the stored preference is the source of truth; without space the sidebar
 * starts collapsed regardless, and a manual open is tracked separately so a
 * cramped window never rewrites the wide-screen preference.
 */
export function useSidebarCollapsedState(
  hasSpaceForSidebar: boolean,
): [boolean, SetCollapsed] {
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage(
    SESSION_SIDEBAR_COLLAPSED_KEY,
    false,
  );

  useEffect(() => {
    window.localStorage.removeItem(LEGACY_COLLAPSED_KEY);
  }, []);

  // Every loss of space re-collapses; a manual open does not survive the
  // round trip through a wide viewport.
  const [openedWhileNarrow, setOpenedWhileNarrow] = useKeyedState(
    hasSpaceForSidebar,
    false,
  );

  const isSidebarCollapsed = hasSpaceForSidebar
    ? storedCollapsed
    : !openedWhileNarrow;

  const setIsSidebarCollapsed = useCallback<SetCollapsed>(
    (value) => {
      if (hasSpaceForSidebar) {
        setStoredCollapsed(value);
        return;
      }
      setOpenedWhileNarrow((prevOpened) => {
        const nextCollapsed =
          typeof value === "function" ? value(!prevOpened) : value;
        return !nextCollapsed;
      });
    },
    [hasSpaceForSidebar, setStoredCollapsed, setOpenedWhileNarrow],
  );

  return [isSidebarCollapsed, setIsSidebarCollapsed];
}
